import { z } from 'zod';
import { Id } from '../primitives.js';

/**
 * Контракты модуля Files. Клиент никогда не обращается к сервису файлов
 * напрямую — только через `/v1/patients/{id}/files` и `/v1/files/{id}` на
 * core-api: право на файл производно от права на пациента, а этими данными
 * владеет core-api. Внутренний протокол `core-api → files` — не часть этих
 * контрактов, он не выходит за пределы серверного кода.
 *
 * Документация модуля: docs/03-modules/11-files-media.md
 */

/**
 * Виды файлов фазы 2.1. DICOM (`cbct`), STL-оттиски (`dental_impression`) и
 * файлы наряда (`lab_attachment`) — не в этой фазе: см. документ модуля.
 */
export const FileKind = z.enum([
  'intraoral_photo',
  'document_scan',
  'signature',
  'panoramic_xray',
  'periapical_xray',
]);
export type FileKind = z.infer<typeof FileKind>;

export const FileStatus = z.enum(['uploading', 'processing', 'ready', 'failed', 'deactivated']);
export type FileStatus = z.infer<typeof FileStatus>;

/** Строка списка файлов пациента: то, что показывает вкладка «Файлы» карты. */
export const PatientFile = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  kind: FileKind,
  status: FileStatus,
  /** Заполнено при отказе обработки — контрольная сумма, тип или антивирус. */
  failureReason: z.string().nullable(),
  createdBy: Id,
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type PatientFile = z.infer<typeof PatientFile>;

/**
 * Начать загрузку. Контрольная сумма — обязательный параметр, не опция: без
 * неё обработка не может отличить повреждённый при передаче файл от целого,
 * а для рентгеновского снимка это не мелочь.
 */
export const CreateUploadSessionRequest = z.object({
  kind: FileKind,
  contentType: z.string().min(1).max(100),
  sizeBytes: z.number().int().positive(),
  checksumSha256: z.string().regex(/^[a-f0-9]{64}$/, 'Ожидается SHA-256 в шестнадцатеричном виде'),
});
export type CreateUploadSessionRequest = z.infer<typeof CreateUploadSessionRequest>;

const UploadPartTarget = z.object({ partNumber: z.number().int().positive(), url: z.url() });

/**
 * Ответ на создание сессии — один из двух видов по размеру файла: `single`
 * несёт одну ссылку на PUT, `multipart` — список ссылок по частям. Различие
 * не декоративное: обрыв на 80% возобновляем только у multipart, потому что
 * только там есть части, каждая со своим подтверждением.
 */
export const CreateUploadSessionResponse = z.discriminatedUnion('mode', [
  z.object({
    fileId: Id,
    mode: z.literal('single'),
    uploadUrl: z.url(),
    expiresAt: z.iso.datetime(),
  }),
  z.object({
    fileId: Id,
    mode: z.literal('multipart'),
    uploadId: z.string().min(1),
    partSize: z.number().int().positive(),
    parts: z.array(UploadPartTarget).min(1),
    expiresAt: z.iso.datetime(),
  }),
]);
export type CreateUploadSessionResponse = z.infer<typeof CreateUploadSessionResponse>;

export const CompleteUploadRequest = z.object({
  parts: z
    .array(
      z.object({
        partNumber: z.number().int().positive(),
        etag: z.string().min(1),
        sizeBytes: z.number().int().positive(),
      }),
    )
    .optional(),
});
export type CompleteUploadRequest = z.infer<typeof CompleteUploadRequest>;

export const CompleteUploadResponse = z.object({ status: z.string() });
export type CompleteUploadResponse = z.infer<typeof CompleteUploadResponse>;

/** Ссылка на файл — оригинал или превью, коротко живущая, привязанная к сессии. */
export const PresignFileResponse = z.union([
  z.object({ status: z.literal('ok'), url: z.url(), expiresAt: z.iso.datetime() }),
  z.object({ status: z.literal('not_ready'), currentStatus: FileStatus }),
]);
export type PresignFileResponse = z.infer<typeof PresignFileResponse>;
